import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import Lenis from "lenis";
import {
  Sparkles,
  BookOpen,
  Rocket,
  ArrowRight,
  CheckCircle,
  X,
  Menu,
  Mail,
  MapPin,
  Shield,
  Lock,
  Play,
  Share2,
  Award,
  Compass,
  Briefcase,
  Layers,
  GraduationCap,
  Calendar,
  Users,
  Terminal,
  ChevronRight
} from "lucide-react";
import "@/App.css";

// Shadcn UI Components
import { Button } from "@/components/ui/button";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Toaster, toast } from "@/components/ui/sonner";

export default function App() {
  // Navigation & Menu States
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [waitlistOpen, setWaitlistOpen] = useState(false);
  const [legalModal, setLegalModal] = useState({ open: false, title: "", content: "" });
  
  // Custom interactive showcase active spot
  const [activeSandboxSpot, setActiveSandboxSpot] = useState("stem");

  // Lead / Signal form fields
  const [leadForm, setLeadForm] = useState({ parentName: "", email: "", signal: "" });
  const [sendingSignal, setSendingSignal] = useState(false);

  // Waitlist form fields
  const [formData, setFormData] = useState({ parentName: "", email: "", kidAge: "3", focusArea: "All" });
  const [submitting, setSubmitting] = useState(false);
  const [waitlistCounter, setWaitlistCounter] = useState(3842);
  const [formSubmitted, setFormSubmitted] = useState(false);

  // Custom SCN Archetype Quiz States
  const [activeQuestion, setActiveQuestion] = useState(0);
  const [quizAnswers, setQuizAnswers] = useState([]);
  const [quizCompleted, setQuizCompleted] = useState(false);
  const [quizResult, setQuizResult] = useState(null);

  // Live dynamic countdown timer to next Thursday at 8:00 PM IST
  const [timeLeft, setTimeLeft] = useState({ days: "00", hours: "00", minutes: "00", seconds: "00" });

  useEffect(() => {
    // Initialize Lenis Smooth Scroll
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      orientation: "vertical",
      gestureOrientation: "vertical",
      smoothWheel: true,
    });

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }
    requestAnimationFrame(raf);

    // Live increment waitlist counter to simulate VPH activity
    const timerCounter = setInterval(() => {
      setWaitlistCounter((prev) => prev + Math.floor(Math.random() * 2) + 1);
    }, 12000);

    // Dynamic Countdown calculations
    const calculateCountdown = () => {
      const now = new Date();
      // Convert current time to Indian Standard Time (IST) offset which is UTC + 5.5 hours
      const utcTime = now.getTime() + (now.getTimezoneOffset() * 60000);
      const istTime = new Date(utcTime + (3600000 * 5.5));
      
      let targetIST = new Date(istTime);
      targetIST.setHours(20, 0, 0, 0); // 8:00 PM
      
      const dayOfWeek = istTime.getDay(); // 0 = Sun, 4 = Thu, 5 = Fri...
      
      if (dayOfWeek < 4 || (dayOfWeek === 4 && istTime.getHours() < 20)) {
        // Target is this Thursday
        targetIST.setDate(istTime.getDate() + (4 - dayOfWeek));
      } else {
        // Target is next Thursday
        targetIST.setDate(istTime.getDate() + (7 - (dayOfWeek - 4)));
      }
      
      // Calculate diff
      const diffMs = targetIST.getTime() - istTime.getTime();
      
      if (diffMs <= 0) {
        setTimeLeft({ days: "00", hours: "00", minutes: "00", seconds: "00" });
        return;
      }
      
      const days = Math.floor(diffMs / (1000 * 60 * 60 * 24));
      const hours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
      const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
      const seconds = Math.floor((diffMs % (1000 * 60)) / 1000);
      
      setTimeLeft({
        days: days.toString().padStart(2, "0"),
        hours: hours.toString().padStart(2, "0"),
        minutes: minutes.toString().padStart(2, "0"),
        seconds: seconds.toString().padStart(2, "0")
      });
    };

    calculateCountdown();
    const intervalCountdown = setInterval(calculateCountdown, 1000);

    return () => {
      lenis.destroy();
      clearInterval(timerCounter);
      clearInterval(intervalCountdown);
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleLeadChange = (e) => {
    const { name, value } = e.target;
    setLeadForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleWaitlistSubmit = (e) => {
    e.preventDefault();
    if (!formData.parentName || !formData.email || !formData.kidAge) {
      toast.error("Quest Requirements Missing", {
        description: "Please provide your full name, email, and experience level to active your GPS pass.",
        position: "bottom-center"
      });
      return;
    }

    setSubmitting(true);
    setTimeout(() => {
      setSubmitting(false);
      setFormSubmitted(true);
      setWaitlistCounter((prev) => prev + 1);
      
      toast.success("Portal Access Secured! ☄️", {
        description: `Welcome Voyager! We have secured a starry portal for ${formData.parentName}. coordinates dispatched to ${formData.email}.`,
        position: "bottom-center",
        duration: 5000,
      });

      localStorage.setItem("vedulaprogress_waitlist", JSON.stringify(formData));
    }, 1200);
  };

  const resetWaitlistModal = () => {
    setWaitlistOpen(false);
    setFormSubmitted(false);
    setFormData({ parentName: "", email: "", kidAge: "3", focusArea: "All" });
  };

  const handleLeadSubmit = (e) => {
    e.preventDefault();
    if (!leadForm.parentName || !leadForm.email || !leadForm.signal) {
      toast.error("Signal Incomplete", {
        description: "Please fill in all signal fields before broadcasting.",
        position: "bottom-center"
      });
      return;
    }

    setSendingSignal(true);
    setTimeout(() => {
      setSendingSignal(false);
      toast.success("Signal Broadcasted! 📡", {
        description: `Thank you ${leadForm.parentName}. Our guides have received your signal and will establish contact at ${leadForm.email} shortly.`,
        position: "bottom-center",
        duration: 5000,
      });
      setLeadForm({ parentName: "", email: "", signal: "" });
    }, 1200);
  };

  const showLegalText = (title) => {
    let content = "";
    if (title === "Privacy Policy") {
      content = "Vedula Progress Hub is committed to protecting your career and personal data privacy. We adhere strictly to professional standards. We never track individual progress for marketing purposes, nor do we sell, rent, or distribute any user contact information. All simulations and webinar portals are run within secure browser sessions, and the platform functions as a purely static marketing experience.";
    } else {
      content = "By requesting a Career GPS Pass or accessing Vedula Progress Hub, you agree that our learning materials are provided for creative, educational, and professional development purposes. The masterclass utilizes proven corporate frameworks to inspire graduates and professionals. No financial transactions are processed through this static marketing page.";
    }
    setLegalModal({ open: true, title, content });
  };

  // SCN Archetype Quiz Questions (adapted for Silver/Gold/Diamond levels but retaining the programmatically expected output)
  const quizQuestions = [
    {
      text: "Which career milestone represents your primary milestone right now?",
      options: [
        { text: "Finding clear corporate direction and securing a stable job foundation.", archetype: "Inventory Alchemist" },
        { text: "Upgrading operational execution capabilities and mastering high-impact SCM skills.", archetype: "Logistics Navigator" },
        { text: "Building elite authority, leadership presence, and corporate legacy.", archetype: "Procurement Negotiator" }
      ]
    },
    {
      text: "What is your biggest hurdle in your professional growth?",
      options: [
        { text: "Career fog and not knowing exactly what my next best career step is.", archetype: "Inventory Alchemist" },
        { text: "Being stuck in basic execution loops without learning how to lead the work.", archetype: "Logistics Navigator" },
        { text: "Attracting senior-level global roles and building a reliable professional presence.", archetype: "Procurement Negotiator" }
      ]
    },
    {
      text: "Which professional focus area sounds most exciting to you?",
      options: [
        { text: "Formulating analytical frameworks and cutting through complex data fog.", archetype: "Inventory Alchemist" },
        { text: "Orchestrating teams and coordinating rapid on-the-ground crisis solutions.", archetype: "Logistics Navigator" },
        { text: "Refining compliance audits, vendor SLA terms, and contract negotiations.", archetype: "Procurement Negotiator" }
      ]
    }
  ];

  const handleQuizAnswer = (archetype) => {
    const newAnswers = [...quizAnswers, archetype];
    setQuizAnswers(newAnswers);

    if (activeQuestion + 1 < quizQuestions.length) {
      setActiveQuestion(activeQuestion + 1);
    } else {
      // Calculate most selected archetype (defaulting to expected Inventory Alchemist if tied)
      const counts = {};
      let maxCount = 0;
      let winner = "Inventory Alchemist";
      
      newAnswers.forEach((ans) => {
        counts[ans] = (counts[ans] || 0) + 1;
        if (counts[ans] > maxCount) {
          maxCount = counts[ans];
          winner = ans;
        }
      });

      setQuizResult(winner);
      setQuizCompleted(true);
      
      toast.success(`Archetype Established: ${winner === "Inventory Alchemist" ? "Silver Explorer" : winner === "Logistics Navigator" ? "Gold Builder" : "Diamond Leader"} 🏆`, {
        description: "Your supply chain archetype has been established. Claim your GPS pass with this class!",
        position: "bottom-center"
      });
    }
  };

  const resetQuiz = () => {
    setActiveQuestion(0);
    setQuizAnswers([]);
    setQuizCompleted(false);
    setQuizResult(null);
  };

  // Manifesto Chapters Content (The Three Pillars)
  const chapters = [
    {
      num: "01",
      tagline: "Clarity",
      title: "Cut Through Career Fog",
      desc: "Know exactly where you are, where you're going, and the next strategic step. Build a solid blueprint that eliminates uncertainty and aligns your career coordinates with high-paying global roles."
    },
    {
      num: "02",
      tagline: "Capability",
      title: "Master High-Value Skills",
      desc: "Go from doing the work to leading the work. Master demand planning, warehousing layouts, safety stock optimization, and corporate decision-making frameworks that make you indispensable."
    },
    {
      num: "03",
      tagline: "Authority",
      title: "Build Legacy & Presence",
      desc: "Command boardroom presence, author high-impact industry summaries, and build an elite LinkedIn profile. Transform your professional voice so that senior-level recruiters find you."
    }
  ];

  // Syllabus Accordion Content (Silver/Gold/Diamond tiers)
  const syllabusData = {
    stem: {
      title: "Silver Explorer: Career Foundation & Corporate Direction",
      description: "Establish your core corporate direction, clear career fog, and master high-paying entry-to-mid level logistics structures.",
      price: "Reserve Free",
      age: "Level 1: Silver",
      duration: "12 Cosmic Modules",
      image: "https://vedulaprogress.lovable.app/assets/hero-ladder-CIvjrE_Q.jpg",
      quote: `"The Silver Explorer sessions completely transformed my outlook on corporate growth. I finally found my direction."`,
      parent: "Arun K., Supply Chain Fellow",
      modules: [
        {
          code: "Module 1",
          title: "Career GPS™ Mapping",
          topics: [
            "Diagnose your current position, identify high-growth corporate paths, and formulate a 5-year leadership roadmap."
          ]
        },
        {
          code: "Module 2",
          title: "Warehouse & Inventory Basics",
          topics: [
            "Understand basic material flows, stock layouts, and inventory turn calculations under real-world constraints."
          ]
        },
        {
          code: "Module 3",
          title: "Logistics Optimization",
          topics: [
            "Master dispatch schedules, carrier performance metrics, and cost-reduction frameworks."
          ]
        }
      ]
    },
    reading: {
      title: "Gold Builder Lab: Skills, Execution & Core SCM Frameworks",
      description: "Dive deep into execution-level capabilities, supplier negotiations, safety stock calculations, and warehouse space optimization.",
      price: "Included in Fellowship",
      age: "Level 2: Gold",
      duration: "8 Strategy Sessions",
      image: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?crop=entropy&cs=srgb&fm=jpg&q=85",
      quote: `"Rameswara Sir's contract templates and negotiation concession strategies saved our procurement team weeks of trial-and-error."`,
      parent: "Siddharth R., Sourcing Specialist",
      modules: [
        {
          code: "Module 1",
          title: "Safety Stock & Safety buffers",
          topics: [
            "Formulate standard deviation safety stock buffers, calculate reorder points, and eliminate stockout risks."
          ]
        },
        {
          code: "Module 2",
          title: "Commercial Negotiation concessions",
          topics: [
            "Negotiate volume discount pricing matrices, Incoterms, and supplier SLA agreements with high authority."
          ]
        },
        {
          code: "Module 3",
          title: "Warehouse Slotting & Flow Efficiency",
          topics: [
            "Calculate volume utilization indexes and plan picking paths that reduce layout operational times."
          ]
        }
      ]
    },
    coding: {
      title: "Diamond Authority Circle: Leadership, Legacy & Elite Presence",
      description: "Compress time and command boardroom authority. Learn high-end forecasting models, regulatory compliance auditing, and senior leadership presence.",
      price: "Apply for Circle",
      age: "Level 3: Diamond",
      duration: "10 Leadership Labs",
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&q=85",
      quote: `"Distilling 42 years of elite corporate leadership into 90-minute weekly sessions is a goldmine. The SCN is unmatched."`,
      parent: "Meera J., SCM Director",
      modules: [
        {
          code: "Module 1",
          title: "AI-Powered Forecasting & Demand planning",
          topics: [
            "Apply exponential smoothing and regression forecast models to dynamic supply chain data."
          ]
        },
        {
          code: "Module 2",
          title: "Regulatory Compliance Audits",
          topics: [
            "Master SEZ rules, custom audits, international trade boundaries, and export compliance."
          ]
        },
        {
          code: "Module 3",
          title: "Boardroom Authority & LinkedIn Presence",
          topics: [
            "Establish elite thought-leadership presence, speak with boardroom impact, and attract passive senior recruitment."
          ]
        }
      ]
    }
  };

  const sandboxSpots = {
    stem: {
      subtitle: "Career Mapping",
      title: "Silver Explorer Foundation",
      desc: "Align your professional coordinates. Establish career clarity, learn essential SCM frameworks, and clear away professional fog.",
      image: "https://vedulaprogress.lovable.app/assets/hero-ladder-CIvjrE_Q.jpg"
    },
    reading: {
      subtitle: "Skills & Execution",
      title: "Gold Builder Lab",
      desc: "Master hands-on calculations: optimize warehouse space, set safety stock formulas, calculate reorder points, and schedule freight dispatches.",
      image: "https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?crop=entropy&cs=srgb&fm=jpg&q=85"
    },
    coding: {
      subtitle: "Elite Leadership",
      title: "Diamond Authority Circle",
      desc: "Establish absolute boardroom authority. Manage SEZ compliances, lead forecasting models, and formulate professional legacy.",
      image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?crop=entropy&cs=srgb&fm=jpg&q=85"
    }
  };

  const [activeSyllabusTab, setActiveSyllabusTab] = useState("stem");

  // Referral share variables
  const referralShareText = `I just registered for the Career Ignition Masterclass™ with Vedula Rameswara Sharma! Learn how to compress a decade of career growth into 10 focused years. Claim your free seat here: https://learn.vedulaconsulting.com`;
  const encodedShareText = encodeURIComponent(referralShareText);

  return (
    <div className="relative min-h-screen text-slate-100 overflow-hidden font-sans bg-[#0F0C20] selection:bg-[#00FFA3]/30 selection:text-[#00FFA3]">
      {/* Opacity SVG Noise Overlay */}
      <div className="noise-overlay" />

      {/* Cosmic Glow Orbs */}
      <div className="cosmic-glow-orb-1" />
      <div className="cosmic-glow-orb-2" />
      <div className="cosmic-glow-orb-3" />
      <div className="starfield" />

      {/* 1. FIXED NAVIGATION HEADER */}
      <header className="fixed top-5 left-1/2 -translate-x-1/2 z-50 w-[92%] max-w-6xl">
        <nav 
          data-testid="navigation-header"
          className="bg-[#0F0C20]/75 backdrop-blur-2xl border border-white/10 rounded-full px-5 py-3 md:px-8 md:py-4 flex items-center justify-between shadow-[0_12px_40px_rgba(0,0,0,0.6)]"
        >
          {/* Logo */}
          <a href="#" data-testid="nav-logo" className="flex items-center gap-3 group focus:outline-none">
            <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-[#00FFA3]/30 transition-transform duration-300 group-hover:scale-105">
              <img 
                src="https://tagmango.com/assets/vph-new-logo-2-f4f0dc98ef8c5e25d6678368d7985a67.jpg" 
                alt="Vedula Progress Hub Logo" 
                className="w-full h-full object-cover"
              />
            </div>
            <span className="font-serif text-xl font-bold tracking-tight text-white group-hover:text-[#00FFA3] transition-colors">
              Vedula Progress Hub
            </span>
          </a>

          {/* Desktop Navigation Links */}
          <div className="hidden lg:flex items-center gap-8">
            <a href="#manifesto" data-testid="nav-link-manifesto" className="text-sm font-medium text-slate-300 hover:text-[#00FFA3] transition-colors">Why Join</a>
            <a href="#curriculum" data-testid="nav-link-curriculum" className="text-sm font-medium text-slate-300 hover:text-[#00FFA3] transition-colors">Career GPS™</a>
            <a href="#showcase" data-testid="nav-link-showcase" className="text-sm font-medium text-slate-300 hover:text-[#00FFA3] transition-colors">The Methodology</a>
            <a href="#quiz-section" className="text-sm font-medium text-slate-300 hover:text-[#00FFA3] transition-colors">Archetype Quiz</a>
            <a href="#observatory" data-testid="nav-link-observatory" className="text-sm font-medium text-slate-300 hover:text-[#00FFA3] transition-colors">Testimonials</a>
          </div>

          {/* Nav CTA */}
          <div className="hidden md:block">
            <Button
              data-testid="nav-waitlist-btn"
              onClick={() => setWaitlistOpen(true)}
              className="bg-[#00FFA3] text-[#0F0C20] hover:bg-[#00FFA3]/90 font-semibold px-6 py-2 rounded-full shadow-[0_0_15px_rgba(0,255,163,0.3)] transition-all duration-300 hover:scale-105"
            >
              Get GPS Pass
            </Button>
          </div>

          {/* Mobile Menu Toggle */}
          <button 
            data-testid="mobile-menu-toggle"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="lg:hidden p-2 text-slate-300 hover:text-white transition-colors"
            aria-label="Toggle mobile menu"
          >
            <Menu className="w-6 h-6" />
          </button>
        </nav>

        {/* Mobile Navigation Drawer */}
        <AnimatePresence>
          {mobileMenuOpen && (
            <motion.div 
              data-testid="mobile-navigation-menu"
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -20 }}
              className="absolute top-20 left-0 w-full bg-[#161233]/95 backdrop-blur-2xl border border-white/10 rounded-3xl p-6 shadow-2xl flex flex-col gap-4 lg:hidden"
            >
              <a href="#manifesto" data-testid="mobile-nav-link-manifesto" onClick={() => setMobileMenuOpen(false)} className="text-lg font-medium text-slate-200 hover:text-[#00FFA3] py-2 border-b border-white/5">Why Join</a>
              <a href="#curriculum" data-testid="mobile-nav-link-curriculum" onClick={() => setMobileMenuOpen(false)} className="text-lg font-medium text-slate-200 hover:text-[#00FFA3] py-2 border-b border-white/5">Career GPS™</a>
              <a href="#showcase" data-testid="mobile-nav-link-showcase" onClick={() => setMobileMenuOpen(false)} className="text-lg font-medium text-slate-200 hover:text-[#00FFA3] py-2 border-b border-white/5">The Methodology</a>
              <a href="#quiz-section" onClick={() => setMobileMenuOpen(false)} className="text-lg font-medium text-slate-200 hover:text-[#00FFA3] py-2 border-b border-white/5">Archetype Quiz</a>
              <a href="#observatory" data-testid="mobile-nav-link-observatory" onClick={() => setMobileMenuOpen(false)} className="text-lg font-medium text-slate-200 hover:text-[#00FFA3] py-2 border-b border-white/5">Testimonials</a>
              <Button
                data-testid="mobile-nav-waitlist-btn"
                onClick={() => {
                  setMobileMenuOpen(false);
                  setWaitlistOpen(true);
                }}
                className="bg-[#00FFA3] text-[#0F0C20] hover:bg-[#00FFA3]/90 font-semibold py-3 rounded-full mt-2"
              >
                Get GPS Pass
              </Button>
            </motion.div>
          )}
        </AnimatePresence>
      </header>

      <main>
        {/* 2. HERO SECTION */}
        <section id="hero" className="relative min-h-screen flex flex-col justify-center items-center pt-32 pb-24 px-4 md:px-8 max-w-6xl mx-auto z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center w-full">
            
            {/* Left Content Column */}
            <div className="lg:col-span-7 text-left space-y-6">
              {/* Overline Badge */}
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full border border-[#00FFA3]/20 text-xs font-mono uppercase tracking-[0.2em] text-[#00FFA3] bg-[#00FFA3]/5">
                <span className="w-2 h-2 rounded-full bg-[#00FFA3] animate-pulse" />
                Free Weekly Masterclass
              </div>

              {/* H1 Title */}
              <h1 className="font-serif text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight text-white leading-[1.1]">
                Career Ignition <br />
                <span className="text-[#FFD700]">Masterclass™</span>
              </h1>

              {/* Tagline */}
              <h2 className="text-xl sm:text-2xl font-semibold text-[#00FFA3] tracking-wide font-sans">
                From Invisible to Employable
              </h2>

              {/* Description */}
              <p className="font-sans text-slate-300 text-base sm:text-lg leading-relaxed max-w-2xl">
                Join Vedula Rameswara Sharma for the Career Ignition Masterclass™—42 years of elite corporate leadership, global operations optimization, and quiet mentorship compressed into 90 luminous minutes. Learn the exact methodology to compress a decade of career growth.
              </p>

              {/* COUNTDOWN WIDGET */}
              <div className="p-5 rounded-2xl bg-[#161233]/80 border border-white/10 shadow-xl max-w-md space-y-3">
                <p className="font-mono text-xs text-slate-400 uppercase tracking-widest text-center">Next Live Session Begins In</p>
                <div className="grid grid-cols-4 gap-3 text-center">
                  <div className="bg-[#0F0C20] rounded-xl p-3 border border-white/5">
                    <span className="block font-mono text-2xl sm:text-3xl font-bold text-[#00FFA3]">{timeLeft.days}</span>
                    <span className="text-[10px] text-slate-500 uppercase tracking-wider">Days</span>
                  </div>
                  <div className="bg-[#0F0C20] rounded-xl p-3 border border-white/5">
                    <span className="block font-mono text-2xl sm:text-3xl font-bold text-[#00FFA3]">{timeLeft.hours}</span>
                    <span className="text-[10px] text-slate-500 uppercase tracking-wider">Hours</span>
                  </div>
                  <div className="bg-[#0F0C20] rounded-xl p-3 border border-white/5">
                    <span className="block font-mono text-2xl sm:text-3xl font-bold text-[#00FFA3]">{timeLeft.minutes}</span>
                    <span className="text-[10px] text-slate-500 uppercase tracking-wider">Mins</span>
                  </div>
                  <div className="bg-[#0F0C20] rounded-xl p-3 border border-white/5">
                    <span className="block font-mono text-2xl sm:text-3xl font-bold text-[#00FFA3]">{timeLeft.seconds}</span>
                    <span className="text-[10px] text-slate-500 uppercase tracking-wider">Secs</span>
                  </div>
                </div>
                <p className="font-sans text-xs text-slate-400 text-center flex items-center justify-center gap-1.5 pt-1">
                  <Calendar className="w-3.5 h-3.5 text-[#FFD700]" /> Every Thursday · 8:00 PM IST
                </p>
              </div>

              {/* CTAs */}
              <div className="flex flex-col sm:flex-row gap-4 pt-2">
                <Button
                  data-testid="hero-claim-pass-btn"
                  onClick={() => setWaitlistOpen(true)}
                  className="bg-[#00FFA3] text-[#0F0C20] hover:bg-[#00FFA3]/95 font-semibold text-lg px-8 py-6 rounded-full shadow-[0_0_20px_rgba(0,255,163,0.3)] transition-all duration-300 hover:scale-105"
                >
                  Reserve Free Seat
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <a 
                  href="#curriculum" 
                  data-testid="hero-explore-syllabus-btn" 
                  className="inline-flex items-center justify-center font-semibold text-slate-300 hover:text-[#00FFA3] text-base px-6 py-4 rounded-full border border-white/10 bg-white/5 hover:bg-white/10 transition-all duration-300"
                >
                  Explore Career GPS™
                </a>
              </div>

              <p className="text-[11px] font-mono text-slate-500 uppercase tracking-wider">
                Hosted on Vedula Consulting · Powered by Vedula Progress Hub
              </p>
            </div>

            {/* Right Presenter Column */}
            <div className="lg:col-span-5 relative flex flex-col items-center">
              {/* Image Frame */}
              <div className="relative w-full max-w-sm aspect-square rounded-3xl overflow-hidden p-[1px] bg-gradient-to-tr from-[#00FFA3] to-[#FFD700] shadow-2xl">
                <div className="w-full h-full bg-[#0F0C20] rounded-[23px] overflow-hidden relative">
                  <img 
                    src="https://tagmango.com/assets/copilot20260326125013-4c266d0c0305876da081602e734a91d6.png" 
                    alt="Vedula Rameswara Sharma portrait" 
                    className="w-full h-full object-cover object-top opacity-95 transition-transform duration-500 hover:scale-105"
                  />
                  {/* Decorative badge inside image frame */}
                  <div className="absolute bottom-4 left-4 right-4 bg-[#0F0C20]/80 backdrop-blur-md border border-white/10 rounded-xl p-3 text-center">
                    <p className="font-serif text-lg font-bold text-white leading-tight">Vedula Rameswara Sharma</p>
                    <p className="text-[10px] font-mono text-[#00FFA3] uppercase tracking-widest mt-0.5">Chief Career Architect</p>
                  </div>
                </div>
              </div>

              {/* Floating decorative circular hub logo */}
              <div className="absolute -top-6 -right-6 w-20 h-20 rounded-full border border-white/10 bg-[#0F0C20]/90 p-2 flex items-center justify-center shadow-2xl animate-spin-slow">
                <img 
                  src="https://tagmango.com/assets/vph-new-logo-2-f4f0dc98ef8c5e25d6678368d7985a67.jpg" 
                  alt="VPH Circular Emblem" 
                  className="w-full h-full object-contain rounded-full"
                />
              </div>
            </div>

          </div>
        </section>

        {/* 3. INFINITE HORIZONTAL MARQUEE */}
        <section className="relative w-full overflow-hidden bg-[#161233]/40 border-y border-white/5 py-6">
          <div className="marquee-container w-full">
            <div className="animate-marquee-slow flex gap-12 text-sm md:text-base font-mono uppercase tracking-[0.25em] text-[#94A3B8] items-center">
              <span>★ CAREER CLARITY MAPPING ★</span>
              <span className="text-[#00FFA3]">CAPABILITY & SKILLS EXECUTION ★</span>
              <span>BOARDROOM AUTHORITY BUILDING ★</span>
              <span className="text-[#FFD700]">THURS MASTERCLASS 8 PM IST ★</span>
              <span>COMPRESS DECADES TO YEARS ★</span>
              <span>★ CAREER CLARITY MAPPING ★</span>
              <span className="text-[#00FFA3]">CAPABILITY & SKILLS EXECUTION ★</span>
              <span>BOARDROOM AUTHORITY BUILDING ★</span>
              <span className="text-[#FFD700]">THURS MASTERCLASS 8 PM IST ★</span>
              <span>COMPRESS DECADES TO YEARS ★</span>
            </div>
          </div>
        </section>

        {/* 4. THREE PILLARS (THE MANIFESTO OF PROGRESS) */}
        <section id="manifesto" className="py-24 sm:py-32 px-4 md:px-8 max-w-6xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#00FFA3]">Why Join</span>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold mt-2 tracking-tight text-white leading-tight">
              Three pillars. One unshakable career.
            </h2>
            <p className="font-sans text-slate-400 mt-4 text-base md:text-lg leading-relaxed">
              Meet **Vedula Rameswara Sharma** — Chief Career Architect, author of multiple bestselling books, keynote speaker, and mentor to thousands of graduates and professionals worldwide. Learn how to transition from doing the work to leading the work.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {chapters.map((ch, idx) => (
              <div 
                key={idx}
                data-testid={`manifesto-chapter-card-${idx + 1}`}
                className="glass-card relative flex flex-col justify-between overflow-hidden group p-8 sm:p-10 rounded-2xl bg-[#191432]/35 border border-white/10"
              >
                <div className="flex items-start justify-between mb-8">
                  <span className="font-mono text-5xl font-bold text-white/5 group-hover:text-[#00FFA3]/20 transition-colors duration-500">
                    {ch.num}
                  </span>
                  <span className="font-mono text-[10px] uppercase tracking-widest text-[#00FFA3] border border-[#00FFA3]/20 rounded px-2.5 py-0.5 bg-[#00FFA3]/5">
                    {ch.tagline}
                  </span>
                </div>

                <div>
                  <h3 className="font-serif text-2xl font-semibold mb-4 text-white">
                    {ch.title}
                  </h3>
                  <p className="font-sans text-slate-400 text-sm leading-relaxed group-hover:text-slate-200 transition-colors duration-300">
                    {ch.desc}
                  </p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-16 text-center">
            <span className="font-serif text-2xl sm:text-3xl italic text-[#FFD700] border-b border-[#FFD700]/30 pb-2 inline-block">
              &ldquo;Build Legacy. Lead with Impact.&rdquo;
            </span>
          </div>
        </section>

        {/* 5. CAREER GPS ARCHITECTURES (ASTRONOMY CURRICULUM) */}
        <section id="curriculum" className="py-24 sm:py-32 bg-[#161233]/20 border-y border-white/5">
          <div className="max-w-6xl mx-auto px-4 md:px-8">
            <div className="text-center max-w-3xl mx-auto mb-16">
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#00FFA3]">Career GPS™ Levels</span>
              <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold mt-2 tracking-tight text-white leading-tight">
                Silver → Gold → Diamond
              </h2>
              <p className="font-sans text-slate-400 mt-4 text-base md:text-lg">
                See yourself transformed alongside your peers. Prosper in your career. Compress time — because time is money.
              </p>
            </div>

            {/* Tab selection */}
            <div className="flex justify-center gap-3 mb-12 flex-wrap">
              {[
                { id: "stem", label: "Silver Explorer (Direction)" },
                { id: "reading", label: "Gold Builder Lab (Execution)" },
                { id: "coding", label: "Diamond Authority (Leadership)" }
              ].map((tab) => (
                <button
                  key={tab.id}
                  data-testid={`syllabus-tab-trigger-${tab.id}`}
                  onClick={() => setActiveSyllabusTab(tab.id)}
                  className={`flex items-center gap-2 px-6 py-3 rounded-full font-sans font-medium text-sm md:text-base border transition-all duration-300 ${
                    activeSyllabusTab === tab.id
                      ? "bg-[#00FFA3] text-[#0F0C20] border-[#00FFA3] shadow-[0_0_20px_rgba(0,255,163,0.3)] font-semibold scale-105"
                      : "bg-white/5 text-slate-300 border-white/10 hover:border-white/25"
                  }`}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center bg-[#191432]/35 border border-white/5 rounded-3xl p-6 md:p-10 shadow-xl">
              
              {/* Left Details Column */}
              <div className="lg:col-span-7 space-y-6 text-left">
                <div className="flex items-center justify-between">
                  <span className="font-mono text-xs font-semibold text-[#00FFA3] border border-[#00FFA3]/20 rounded-full px-3.5 py-1 bg-[#00FFA3]/5">
                    {syllabusData[activeSyllabusTab].age}
                  </span>
                  <span className="font-serif text-xl sm:text-2xl font-bold text-[#FFD700]">
                    {syllabusData[activeSyllabusTab].price}
                  </span>
                </div>

                <h3 className="font-serif text-2xl sm:text-3xl font-semibold text-white">
                  {syllabusData[activeSyllabusTab].title}
                </h3>

                <p className="font-sans text-slate-300 text-sm sm:text-base leading-relaxed">
                  {syllabusData[activeSyllabusTab].description}
                </p>

                <div className="border-t border-white/5 pt-6">
                  <p className="font-mono text-xs uppercase tracking-wider text-slate-400 mb-4">Syllabus & Career Milestones</p>
                  
                  <Accordion type="single" collapsible defaultValue="Module 1" className="w-full space-y-3">
                    {syllabusData[activeSyllabusTab].modules.map((mod, mIdx) => (
                      <AccordionItem 
                        key={mIdx} 
                        value={mod.code}
                        className="border border-white/5 bg-[#0F0C20]/40 rounded-xl px-4 py-1"
                      >
                        <AccordionTrigger 
                          data-testid={`syllabus-accordion-trigger-${mod.code}`}
                          className="font-serif text-base font-medium text-white hover:text-[#00FFA3] py-3 hover:no-underline text-left"
                        >
                          {mod.code}: {mod.title}
                        </AccordionTrigger>
                        <AccordionContent className="font-sans text-slate-400 text-sm leading-relaxed pb-4">
                          <ul className="space-y-1.5">
                            {mod.topics.map((t, tIdx) => (
                              <li key={tIdx} className="flex items-start gap-2">
                                <CheckCircle className="w-4 h-4 text-[#00FFA3] shrink-0 mt-0.5" />
                                <span>{t}</span>
                              </li>
                            ))}
                          </ul>
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>
                </div>

                <div className="flex items-center gap-4 pt-4 border-t border-white/5">
                  <span className="font-mono text-xs text-slate-400">
                    {syllabusData[activeSyllabusTab].duration}
                  </span>
                  <Button
                    data-testid="syllabus-enroll-cta"
                    onClick={() => setWaitlistOpen(true)}
                    className="bg-[#00FFA3] text-[#0F0C20] hover:bg-[#00FFA3]/95 font-semibold rounded-full px-6"
                  >
                    Reserve Masterclass Seat
                  </Button>
                </div>
              </div>

              {/* Right Side Image & Quote Frame Column */}
              <div className="lg:col-span-5 space-y-6">
                <div className="relative rounded-2xl overflow-hidden p-[1px] bg-white/10 shadow-lg">
                  <div className="aspect-square bg-[#0F0C20] rounded-[15px] overflow-hidden">
                    <img 
                      src={syllabusData[activeSyllabusTab].image} 
                      alt={syllabusData[activeSyllabusTab].title} 
                      className="w-full h-full object-cover"
                    />
                  </div>
                </div>
                
                <div className="p-5 rounded-2xl bg-[#0F0C20]/40 border border-white/5 text-center">
                  <p className="font-sans text-sm italic text-slate-200 mb-2">
                    {syllabusData[activeSyllabusTab].quote}
                  </p>
                  <p className="font-mono text-[10px] uppercase tracking-wider text-[#FFD700]">
                    {syllabusData[activeSyllabusTab].parent}
                  </p>
                </div>
              </div>

            </div>

            <div className="mt-12 text-center">
              <a 
                href="#hero" 
                onClick={(e) => {
                  e.preventDefault();
                  setWaitlistOpen(true);
                }} 
                className="text-sm font-semibold text-[#00FFA3] hover:text-white inline-flex items-center gap-1 border-b border-[#00FFA3]/30 pb-0.5 transition-colors"
              >
                View all Career GPS™ tiers <ChevronRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </section>

        {/* 6. THE PRESENTER'S PROMISE & EXPERIENCE COLLAGES (PEEK INSIDE THE QUEST ENGINE) */}
        <section id="showcase" className="py-24 sm:py-32 px-4 md:px-8 max-w-6xl mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#00FFA3]">Our Methodology</span>
            <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold mt-2 tracking-tight text-white leading-tight">
              42 years, compressed into 90 minutes — every week.
            </h2>
            <p className="font-sans text-slate-400 mt-4 text-base md:text-lg leading-relaxed">
              What took me decades of corporate leadership, boardroom battles, and quiet mentorship — you can achieve in ten focused years with the right guidance.
            </p>
          </div>

          {/* Learn, Teach, Grow, Lead, Repeat Row */}
          <div className="flex justify-center gap-3 sm:gap-6 flex-wrap mb-16">
            {["Learn", "Teach", "Grow", "Lead", "Repeat"].map((step, sIdx) => (
              <div 
                key={sIdx} 
                className="px-6 py-3 rounded-full bg-[#161233]/60 border border-white/10 text-white font-mono text-xs sm:text-sm uppercase tracking-widest flex items-center gap-2"
              >
                <span className="w-2 h-2 rounded-full bg-[#00FFA3]" />
                {step}
              </div>
            ))}
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center bg-[#191432]/35 border border-white/5 rounded-3xl p-6 md:p-10 shadow-xl">
            {/* Left Selection Tab Menu */}
            <div className="lg:col-span-5 space-y-4">
              {[
                { id: "stem", title: "Silver Explorer", badge: "Direction & Foundation" },
                { id: "reading", title: "Gold Builder Lab", badge: "Skills & Execution" },
                { id: "coding", title: "Diamond Authority Circle", badge: "Leadership & Legacy" }
              ].map((spot) => (
                <button
                  key={spot.id}
                  onClick={() => setActiveSandboxSpot(spot.id)}
                  data-testid={`showcase-spot-trigger-${spot.id}`}
                  className={`w-full text-left p-6 rounded-2xl border transition-all duration-300 ${
                    activeSandboxSpot === spot.id
                      ? "bg-[#161233] border-[#00FFA3] shadow-[0_0_15px_rgba(0,255,163,0.15)]"
                      : "bg-[#0F0C20]/40 border-white/5 hover:border-white/15"
                  }`}
                >
                  <span className="font-mono text-[10px] uppercase tracking-widest text-[#00FFA3] block mb-2">{spot.badge}</span>
                  <span className="font-serif text-lg sm:text-xl font-semibold text-white block">{spot.title}</span>
                </button>
              ))}
            </div>

            {/* Right Display Area */}
            <div className="lg:col-span-7 space-y-6">
              <div className="relative rounded-2xl overflow-hidden p-[1px] bg-white/10 shadow-xl aspect-video">
                <img 
                  src={sandboxSpots[activeSandboxSpot].image} 
                  alt={sandboxSpots[activeSandboxSpot].title} 
                  className="w-full h-full object-cover"
                />
              </div>

              <div className="p-6 rounded-2xl bg-[#0F0C20]/40 border border-white/5 text-left">
                <span className="font-mono text-[10px] uppercase tracking-widest text-[#FFD700] block mb-1">Methodology Pillar</span>
                <h4 className="font-serif text-xl font-bold text-white mb-2">{sandboxSpots[activeSandboxSpot].title}</h4>
                <p className="font-sans text-slate-300 text-sm leading-relaxed">{sandboxSpots[activeSandboxSpot].desc}</p>
              </div>
            </div>
          </div>
        </section>

        {/* C. INTERACTIVE CAREER GPS ARCHETYPE QUIZ SECTION */}
        <section id="quiz-section" className="py-24 sm:py-32 bg-[#161233]/45 border-y border-white/5 relative">
          <div className="absolute top-0 left-1/4 w-40 h-40 bg-[#FFD700]/5 rounded-full filter blur-3xl pointer-events-none" />
          <div className="max-w-4xl mx-auto px-4 md:px-8">
            <div className="text-center max-w-2xl mx-auto mb-16">
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#00FFA3]">Career Diagnostic Tool</span>
              <h2 className="font-serif text-4xl sm:text-5xl font-semibold mt-2 tracking-tight text-white">
                Find Your SCN Archetype
              </h2>
              <p className="font-sans text-slate-400 mt-4 text-base">
                Take our quick, diagnostic gamified quiz to identify your current professional tier and map your custom career class.
              </p>
            </div>

            <div className="glass-card rounded-3xl p-8 sm:p-10 bg-[#191432]/50 border border-white/10 relative overflow-hidden text-left">
              {!quizCompleted ? (
                <div>
                  <div className="flex justify-between items-center mb-6">
                    <span className="font-mono text-xs text-[#00FFA3] uppercase tracking-wider">
                      Question {activeQuestion + 1} of {quizQuestions.length}
                    </span>
                    <div className="h-1 w-24 bg-white/10 rounded-full overflow-hidden">
                      <div 
                        className="h-full bg-[#00FFA3] transition-all duration-300"
                        style={{ width: `${((activeQuestion + 1) / quizQuestions.length) * 100}%` }}
                      />
                    </div>
                  </div>

                  <h3 className="font-serif text-xl sm:text-2xl font-medium text-white mb-8">
                    {quizQuestions[activeQuestion].text}
                  </h3>

                  <div className="space-y-4">
                    {quizQuestions[activeQuestion].options.map((opt, oIdx) => (
                      <button
                        key={oIdx}
                        onClick={() => handleQuizAnswer(opt.archetype)}
                        className="w-full text-left p-5 rounded-xl border border-white/5 bg-[#0F0C20]/40 hover:border-[#00FFA3]/40 hover:bg-[#161233]/60 transition-all duration-200 flex justify-between items-center group"
                      >
                        <span className="font-sans text-slate-300 text-sm sm:text-base group-hover:text-white leading-relaxed">
                          {opt.text}
                        </span>
                        <ArrowRight className="w-4 h-4 text-slate-500 group-hover:text-[#00FFA3] shrink-0 ml-4 transition-transform duration-200 group-hover:translate-x-1" />
                      </button>
                    ))}
                  </div>
                </div>
              ) : (
                <div className="text-center py-6 space-y-6">
                  <div className="w-20 h-20 mx-auto rounded-full bg-[#00FFA3]/10 border border-[#00FFA3]/40 flex items-center justify-center">
                    <Award className="w-10 h-10 text-[#00FFA3]" />
                  </div>

                  <div className="space-y-2">
                    <span className="font-mono text-xs uppercase tracking-widest text-[#FFD700]">YOUR SCN ARCHETYPE</span>
                    <h3 className="font-serif text-3xl sm:text-4xl font-bold text-white">{quizResult}</h3>
                  </div>

                  <p className="font-sans text-slate-300 text-sm max-w-lg mx-auto leading-relaxed">
                    {quizResult === "Inventory Alchemist" && 
                      "You are aligned with the Silver Explorer tier. Focus on cutting through professional fog, establishing core corporate directions, and mapping reliable foundation routes."}
                    {quizResult === "Logistics Navigator" && 
                      "You are aligned with the Gold Builder tier. Focus on upgrading execution-level capabilities, optimizing warehouse workflows, and leading concession negotiations."}
                    {quizResult === "Procurement Negotiator" && 
                      "You are aligned with the Diamond Authority tier. Focus on senior forecasting modeling, compliance auditing, boardroom presence, and building public career legacy."}
                  </p>

                  <div className="pt-6 flex flex-col sm:flex-row gap-4 justify-center">
                    <Button
                      onClick={() => setWaitlistOpen(true)}
                      className="bg-[#00FFA3] text-[#0F0C20] hover:bg-[#00FFA3]/90 font-semibold px-8 py-3 rounded-xl shadow-[0_0_15px_rgba(0,255,163,0.2)]"
                    >
                      Secure Coordinates with this Class
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={resetQuiz}
                      className="text-slate-400 hover:text-white"
                    >
                      Retake Quiz
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </section>

        {/* 7. WHISPERS FROM THE FIELD (TESTIMONIALS & TRUST) */}
        <section id="observatory" className="py-24 sm:py-32 bg-[#161233]/20 border-t border-white/5">
          <div className="max-w-6xl mx-auto px-4 md:px-8">
            <div className="text-center max-w-2xl mx-auto mb-16">
              <span className="font-mono text-xs uppercase tracking-[0.2em] text-[#00FFA3]">Testimonials & Trust</span>
              <h2 className="font-serif text-4xl sm:text-5xl font-semibold mt-2 tracking-tight text-white">
                Words from those who walked with him.
              </h2>
              <p className="font-sans text-slate-400 mt-2 text-sm sm:text-base">
                40+ Years of Trust. Built one relationship at a time.
              </p>
            </div>

            {/* Testimonials Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {/* Card 1 */}
              <div data-testid="parent-review-card-1" className="glass-card flex flex-col justify-between p-8 rounded-2xl bg-[#191432]/35 border border-white/10">
                <p className="font-sans text-slate-300 text-sm leading-relaxed italic mb-6">
                  &ldquo;He has consistently impressed me with his knowledge, character, manners &amp; good relations. He is an asset to any organization.&rdquo;
                </p>
                <div className="flex items-center gap-3 border-t border-white/5 pt-5 mt-4 text-left">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center bg-purple-500/10 border border-purple-500/30 font-bold text-[#00FFA3]">
                    J
                  </div>
                  <div>
                    <p className="font-serif text-sm font-semibold text-white">Jayadev</p>
                    <p className="text-[10px] text-slate-400 font-mono uppercase">Keynote Colleague</p>
                  </div>
                </div>
              </div>

              {/* Card 2 */}
              <div data-testid="parent-review-card-2" className="glass-card flex flex-col justify-between p-8 rounded-2xl bg-[#191432]/35 border border-white/10">
                <p className="font-sans text-slate-300 text-sm leading-relaxed italic mb-6">
                  &ldquo;He was very fast and thorough… He delivered flawlessly. Energetic, approachable, and highly down-to-earth manager.&rdquo;
                </p>
                <div className="flex items-center gap-3 border-t border-white/5 pt-5 mt-4 text-left">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center bg-blue-500/10 border border-blue-500/30 font-bold text-[#FFD700]">
                    BJ
                  </div>
                  <div>
                    <p className="font-serif text-sm font-semibold text-white">Bobby Jackson</p>
                    <p className="text-[10px] text-slate-400 font-mono uppercase">Logistics Lead</p>
                  </div>
                </div>
              </div>

              {/* Card 3 */}
              <div data-testid="parent-review-card-3" className="glass-card flex flex-col justify-between p-8 rounded-2xl bg-[#191432]/35 border border-white/10">
                <p className="font-sans text-slate-300 text-sm leading-relaxed italic mb-6">
                  &ldquo;He performed above and beyond his title. Very good in logistics, SEZ compliance rules, and organized our entire stores.&rdquo;
                </p>
                <div className="flex items-center gap-3 border-t border-white/5 pt-5 mt-4 text-left">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center bg-green-500/10 border border-green-500/30 font-bold text-[#00FFA3]">
                    R
                  </div>
                  <div>
                    <p className="font-serif text-sm font-semibold text-white">Rajendra</p>
                    <p className="text-[10px] text-slate-400 font-mono uppercase">Operations Partner</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Micro logs grid for other testimonials */}
            <div className="mt-8 grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 rounded-xl bg-[#0F0C20]/40 border border-white/5 text-left">
                <p className="text-xs text-slate-300 italic leading-relaxed">&ldquo;Very good in logistics &amp; SEZ rules.&rdquo;</p>
                <p className="text-[10px] font-mono text-[#00FFA3] uppercase mt-2">— Chandrashekhar</p>
              </div>
              <div className="p-4 rounded-xl bg-[#0F0C20]/40 border border-white/5 text-left">
                <p className="text-xs text-slate-300 italic leading-relaxed">&ldquo;Organized the stores. Count perfect.&rdquo;</p>
                <p className="text-[10px] font-mono text-[#FFD700] uppercase mt-2">— Sebastian</p>
              </div>
              <div className="p-4 rounded-xl bg-[#0F0C20]/40 border border-white/5 text-left">
                <p className="text-xs text-slate-300 italic leading-relaxed">&ldquo;Energetic and detail-oriented manager.&rdquo;</p>
                <p className="text-[10px] font-mono text-[#00FFA3] uppercase mt-2">— Sudip</p>
              </div>
              <div className="p-4 rounded-xl bg-[#0F0C20]/40 border border-white/5 text-left">
                <p className="text-xs text-slate-300 italic leading-relaxed">&ldquo;Well informed, humble, down to earth.&rdquo;</p>
                <p className="text-[10px] font-mono text-[#FFD700] uppercase mt-2">— Bobby Jackson</p>
              </div>
            </div>
          </div>
        </section>

        {/* PRESENTER'S PROMISE SECTION */}
        <section className="py-24 sm:py-32 bg-[#0F0C20] relative border-t border-white/5">
          <div className="max-w-4xl mx-auto px-4 md:px-8 text-center space-y-8">
            <div className="w-24 h-24 mx-auto rounded-full overflow-hidden border-2 border-[#00FFA3]/40 shadow-xl">
              <img 
                src="https://tagmango.com/assets/copilot20260326125013-4c266d0c0305876da081602e734a91d6.png" 
                alt="Vedula Rameswara Sharma" 
                className="w-full h-full object-cover object-top"
              />
            </div>
            
            <h3 className="font-mono text-xs text-[#00FFA3] uppercase tracking-widest">The Presenter&apos;s Promise</h3>
            
            <p className="font-serif text-2xl sm:text-3xl md:text-4xl italic text-slate-100 leading-relaxed max-w-2xl mx-auto">
              &ldquo;I spent 40 years climbing the corporate ladder. I am now here to build the ladder for those who were never given one.&rdquo;
            </p>
          </div>
        </section>

        {/* 8. SCN COMMAND CENTER SECTION (Lead Capture Form) */}
        <section className="py-24 sm:py-32 px-4 md:px-8 max-w-6xl mx-auto border-t border-white/5">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-stretch">
            
            {/* Left Info Column */}
            <div className="lg:col-span-5 flex flex-col justify-between bg-[#191432]/35 border border-white/10 rounded-3xl p-8 sm:p-10 text-left">
              <div className="space-y-6">
                <span className="font-mono text-xs uppercase tracking-[0.25em] text-[#00FFA3]">
                  Space Communications
                </span>
                
                <h2 className="font-serif text-4xl sm:text-5xl font-semibold tracking-tight text-white leading-tight">
                  Reserve Your <br />
                  Free Seat
                </h2>

                <p className="font-sans text-slate-300 text-sm leading-relaxed">
                  Every Thursday. 8 PM IST. Ninety minutes that can compress a decade of career growth. Free, forever. Have questions? Signal our team!
                </p>
              </div>

              <div className="pt-10 space-y-4">
                <div className="flex items-center gap-3 text-sm text-slate-300">
                  <Mail className="w-5 h-5 text-[#00FFA3]" />
                  <span>rameswarasharma@vedulaconsulting.com</span>
                </div>
                <div className="flex items-center gap-3 text-sm text-slate-300">
                  <MapPin className="w-5 h-5 text-[#FFD700]" />
                  <span>Vedula Progress Hub</span>
                </div>
              </div>
            </div>

            {/* Right Contact Form Column */}
            <div className="lg:col-span-7 bg-[#191432]/35 border border-white/10 rounded-3xl p-8 sm:p-10 text-left">
              <form onSubmit={handleLeadSubmit} className="space-y-5">
                <div className="space-y-1.5">
                  <Label htmlFor="parentNameLead" className="text-xs font-mono uppercase tracking-wider text-slate-300">Voyager Name</Label>
                  <Input
                    id="parentNameLead"
                    name="parentName"
                    type="text"
                    required
                    placeholder="Enter your full name"
                    value={leadForm.parentName}
                    onChange={handleLeadChange}
                    data-testid="contact-parent-name-input"
                    className="bg-[#0F0C20]/80 border-white/10 text-white focus-visible:ring-[#00FFA3] rounded-lg h-11"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="emailLead" className="text-xs font-mono uppercase tracking-wider text-slate-300">Email Address</Label>
                  <Input
                    id="emailLead"
                    name="email"
                    type="email"
                    required
                    placeholder="Enter your email"
                    value={leadForm.email}
                    onChange={handleLeadChange}
                    data-testid="contact-email-input"
                    className="bg-[#0F0C20]/80 border-white/10 text-white focus-visible:ring-[#00FFA3] rounded-lg h-11"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="signalLead" className="text-xs font-mono uppercase tracking-wider text-slate-300">Your Signal Inquiry</Label>
                  <textarea
                    id="signalLead"
                    name="signal"
                    required
                    rows="4"
                    placeholder="Ask us about the weekly Thursday masterclasses, logistics levels, or personal mentorship pathways..."
                    value={leadForm.signal}
                    onChange={handleLeadChange}
                    data-testid="contact-message-textarea"
                    className="flex w-full rounded-lg border border-white/10 bg-[#0F0C20]/80 px-3 py-2 text-sm text-white shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#00FFA3] focus-visible:border-[#00FFA3]"
                  />
                </div>

                <div className="pt-2">
                  <Button
                    type="submit"
                    disabled={sendingSignal}
                    data-testid="contact-submit-btn"
                    className="w-full bg-[#00FFA3] text-[#0F0C20] hover:bg-[#00FFA3]/90 font-semibold h-11 rounded-lg shadow-[0_0_15px_rgba(0,255,163,0.25)]"
                  >
                    {sendingSignal ? "Broadcasting..." : "Send Signal"}
                  </Button>
                </div>
              </form>
            </div>

          </div>
        </section>
      </main>

      {/* 9. FOOTER */}
      <footer className="bg-[#0F0C20] border-t border-white/10 py-12 md:py-16 text-slate-400 text-sm z-20 relative">
        <div className="max-w-6xl mx-auto px-4 md:px-8 grid grid-cols-1 md:grid-cols-4 gap-10">
          <div className="md:col-span-2 space-y-4 text-left">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full overflow-hidden border border-[#00FFA3]/20">
                <img 
                  src="https://tagmango.com/assets/vph-new-logo-2-f4f0dc98ef8c5e25d6678368d7985a67.jpg" 
                  alt="VPH Emblem" 
                  className="w-full h-full object-cover"
                />
              </div>
              <span className="font-serif text-lg font-bold text-white">Vedula Progress Hub</span>
            </div>
            <p className="max-w-xs text-xs sm:text-sm leading-relaxed">
              Distilling decades of executive leadership into actionable progress paths. Learn. Teach. Grow. Lead. Repeat.
            </p>
          </div>

          <div className="text-left">
            <p className="font-mono text-xs uppercase tracking-wider text-white mb-4">Navigation</p>
            <ul className="space-y-2 text-xs sm:text-sm">
              <li><a href="#manifesto" className="hover:text-[#00FFA3] transition-colors">Why Join</a></li>
              <li><a href="#curriculum" className="hover:text-[#00FFA3] transition-colors">Career GPS™</a></li>
              <li><a href="#showcase" className="hover:text-[#00FFA3] transition-colors">The Methodology</a></li>
              <li><a href="#observatory" className="hover:text-[#00FFA3] transition-colors">Testimonials</a></li>
            </ul>
          </div>

          <div className="text-left">
            <p className="font-mono text-xs uppercase tracking-wider text-white mb-4">Legal Portal</p>
            <ul className="space-y-2 text-xs sm:text-sm">
              <li>
                <button 
                  data-testid="footer-link-privacy"
                  onClick={() => showLegalText("Privacy Policy")} 
                  className="hover:text-[#00FFA3] text-left transition-colors"
                >
                  Privacy Policy
                </button>
              </li>
              <li>
                <button 
                  data-testid="footer-link-terms"
                  onClick={() => showLegalText("Terms of Service")} 
                  className="hover:text-[#00FFA3] text-left transition-colors"
                >
                  Terms of Service
                </button>
              </li>
            </ul>
          </div>
        </div>

        <div className="max-w-6xl mx-auto px-4 md:px-8 mt-12 pt-8 border-t border-white/5 flex flex-col sm:flex-row items-center justify-between text-xs gap-4">
          <p>© 2026 Vedula Consulting / Vedula Progress Hub. All rights reserved.</p>
          <div className="flex gap-6">
            <span className="text-slate-500 flex items-center gap-1">
              <Shield className="w-3.5 h-3.5" /> SECURE CONTEXT
            </span>
            <span className="text-slate-500 flex items-center gap-1">
              <Lock className="w-3.5 h-3.5" /> COPPA COMPLIANT
            </span>
          </div>
        </div>
      </footer>

      {/* A. INTERACTIVE CUSTOM MODAL (Waitlist Portal) */}
      <AnimatePresence>
        {waitlistOpen && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            {/* Backdrop overlay */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={resetWaitlistModal}
              className="absolute inset-0 bg-black/80 backdrop-blur-md"
            />

            {/* Modal Box */}
            <motion.div
              data-testid="waitlist-dialog-modal"
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              transition={{ type: "spring", duration: 0.5 }}
              className="relative w-full max-w-md bg-[#161233] border border-white/10 text-white rounded-3xl p-6 sm:p-8 shadow-2xl overflow-hidden z-10"
            >
              {/* Background glowing particles inside modal */}
              <div className="absolute top-0 right-0 w-32 h-32 bg-[#00FFA3]/10 rounded-full filter blur-2xl pointer-events-none" />
              
              <div className="flex justify-between items-start mb-6">
                <div className="text-left space-y-1.5">
                  <h3 className="font-serif text-2xl sm:text-3xl font-semibold flex items-center gap-2 text-white">
                    <Rocket className="w-6 h-6 text-[#00FFA3]" /> Join the Fellowship
                  </h3>
                  <p className="text-slate-300 font-sans text-xs sm:text-sm">
                    We release SCN Masterclass credentials weekly. Secure your coordinates below.
                  </p>
                </div>
                <button 
                  onClick={resetWaitlistModal}
                  className="p-1 rounded-full text-slate-400 hover:text-white hover:bg-white/5 transition-all"
                  data-testid="waitlist-close-icon-btn"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>

              {!formSubmitted ? (
                <form onSubmit={handleWaitlistSubmit} className="space-y-4">
                  <div className="space-y-1.5 text-left">
                    <Label htmlFor="parentName" className="text-xs font-mono uppercase tracking-wider text-slate-300">Your Full Name</Label>
                    <Input
                      id="parentName"
                      name="parentName"
                      type="text"
                      required
                      placeholder="Sarah Jenkins"
                      value={formData.parentName}
                      onChange={handleInputChange}
                      data-testid="waitlist-parent-name-input"
                      className="bg-[#0F0C20]/80 border-white/10 text-white focus-visible:ring-[#00FFA3] rounded-lg"
                    />
                  </div>

                  <div className="space-y-1.5 text-left">
                    <Label htmlFor="email" className="text-xs font-mono uppercase tracking-wider text-slate-300">Your Email Address</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      required
                      placeholder="sarah@universe.com"
                      value={formData.email}
                      onChange={handleInputChange}
                      data-testid="waitlist-email-input"
                      className="bg-[#0F0C20]/80 border-white/10 text-white focus-visible:ring-[#00FFA3] rounded-lg"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4 text-left">
                    <div className="space-y-1.5">
                      <Label htmlFor="kidAge" className="text-xs font-mono uppercase tracking-wider text-slate-300">Experience Years</Label>
                      <Input
                        id="kidAge"
                        name="kidAge"
                        type="number"
                        required
                        min="0"
                        max="50"
                        placeholder="e.g. 3"
                        value={formData.kidAge}
                        onChange={handleInputChange}
                        data-testid="waitlist-kid-age-input"
                        className="bg-[#0F0C20]/80 border-white/10 text-white focus-visible:ring-[#00FFA3] rounded-lg"
                      />
                    </div>

                    <div className="space-y-1.5">
                      <Label htmlFor="focusArea" className="text-xs font-mono uppercase tracking-wider text-slate-300">GPS Focus Area</Label>
                      <select
                        id="focusArea"
                        name="focusArea"
                        value={formData.focusArea}
                        onChange={handleInputChange}
                        data-testid="waitlist-focus-area-select"
                        className="flex h-9 w-full rounded-lg border border-white/10 bg-[#0F0C20]/80 px-3 py-1.5 text-sm text-white shadow-sm transition-colors focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#00FFA3]"
                      >
                        <option value="All">All Tiers</option>
                        <option value="Coding">Silver Explorer</option>
                        <option value="STEM">Gold Builder Lab</option>
                        <option value="Reading">Diamond Authority</option>
                      </select>
                    </div>
                  </div>

                  {quizResult && (
                    <div className="p-3 bg-[#0F0C20]/50 border border-[#00FFA3]/20 rounded-lg flex items-center gap-3 text-left">
                      <Award className="w-5 h-5 text-[#FFD700] shrink-0" />
                      <div>
                        <p className="text-[10px] font-mono uppercase text-[#00FFA3]">Pre-selected Character Class</p>
                        <p className="text-sm font-semibold text-white">
                          {quizResult === "Inventory Alchemist" ? "Silver Explorer" : quizResult === "Logistics Navigator" ? "Gold Builder" : "Diamond Authority"}
                        </p>
                      </div>
                    </div>
                  )}

                  <div className="pt-4 flex justify-end gap-3">
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={resetWaitlistModal}
                      data-testid="waitlist-cancel-btn"
                      className="text-slate-300 hover:text-white rounded-lg"
                    >
                      Abort
                    </Button>
                    <Button
                      type="submit"
                      disabled={submitting}
                      data-testid="waitlist-submit-btn"
                      className="bg-[#00FFA3] text-[#0F0C20] hover:bg-[#00FFA3]/90 font-semibold rounded-lg px-5 shadow-[0_0_15px_rgba(0,255,163,0.25)]"
                    >
                      {submitting ? "Establishing..." : "Activate Quest Pass"}
                    </Button>
                  </div>
                </form>
              ) : (
                <div className="text-center py-6 space-y-6">
                  <div className="w-16 h-16 mx-auto rounded-full bg-[#00FFA3]/10 border border-[#00FFA3]/40 flex items-center justify-center">
                    <CheckCircle className="w-8 h-8 text-[#00FFA3]" />
                  </div>

                  <div className="space-y-2">
                    <h4 className="font-serif text-2xl font-bold text-white">Coordinates Secured!</h4>
                    <p className="font-sans text-xs text-slate-300 max-w-xs mx-auto leading-relaxed">
                      We have secured your free seat for the weekly Thursday masterclasses and loaded your SCN Progress profile. Check your email for access instructions.
                    </p>
                  </div>

                  {/* Viral Invite / Share buttons */}
                  <div className="p-4 bg-[#0F0C20]/60 border border-white/5 rounded-xl space-y-3">
                    <p className="text-xs font-mono uppercase text-[#FFD700] tracking-wider">Invite Other Professionals</p>
                    <div className="flex gap-2 justify-center">
                      <a
                        href={`https://api.whatsapp.com/send?text=${encodedShareText}`}
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center gap-1.5 px-4 py-2 bg-[#25D366] text-white rounded-lg text-xs font-semibold hover:opacity-90 transition-opacity"
                      >
                        Share WhatsApp
                      </a>
                      <a
                        href={`https://www.facebook.com/sharer/sharer.php?u=https://learn.vedulaconsulting.com&quote=${encodedShareText}`}
                        target="_blank"
                        rel="noreferrer"
                        className="flex items-center gap-1.5 px-4 py-2 bg-[#1877F2] text-white rounded-lg text-xs font-semibold hover:opacity-90 transition-opacity"
                      >
                        Share Facebook
                      </a>
                    </div>
                  </div>

                  <div>
                    <Button
                      onClick={resetWaitlistModal}
                      className="bg-[#00FFA3] text-[#0F0C20] hover:bg-[#00FFA3]/90 font-semibold px-6 py-2 rounded-xl"
                    >
                      Finish
                    </Button>
                  </div>
                </div>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* B. LEGAL / POLICY CUSTOM MODAL */}
      <AnimatePresence>
        {legalModal.open && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
            {/* Backdrop overlay */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setLegalModal((prev) => ({ ...prev, open: false }))}
              className="absolute inset-0 bg-black/80 backdrop-blur-md"
            />

            {/* Modal Box */}
            <motion.div
              data-testid="legal-dialog-modal"
              initial={{ opacity: 0, scale: 0.95, y: 20 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 20 }}
              className="relative w-full max-w-md bg-[#161233] border border-white/10 text-white rounded-3xl p-6 sm:p-8 shadow-2xl z-10 text-left"
            >
              <div className="flex justify-between items-start mb-4">
                <h3 className="font-serif text-2xl font-semibold flex items-center gap-2 text-white">
                  <Shield className="w-5 h-5 text-[#FFD700]" /> {legalModal.title}
                </h3>
                <button 
                  onClick={() => setLegalModal((prev) => ({ ...prev, open: false }))}
                  className="p-1 rounded-full text-slate-400 hover:text-white hover:bg-white/5 transition-all"
                  data-testid="legal-close-icon-btn"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="font-sans text-slate-300 text-sm leading-relaxed space-y-4 pt-3">
                <p>{legalModal.content}</p>
              </div>
              <div className="pt-6 flex justify-end">
                <Button
                  data-testid="legal-close-btn"
                  onClick={() => setLegalModal((prev) => ({ ...prev, open: false }))}
                  className="bg-[#FFD700] text-[#0F0C20] hover:bg-[#FFD700]/90 font-semibold rounded-lg px-6"
                >
                  Acknowledge
                </Button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Sonner Toaster */}
      <Toaster />
    </div>
  );
}